/**
 * OpenAI Provider Instance - Simplified for backend-only processing
 * All AI requests are now handled through the backend service
 */
import providerFactory from "@/lib/ai-providers/provider-factory";

// Provider configurations - API keys removed since backend handles all requests
const providers = {
  openai: {
    baseURL: undefined, // Default OpenAI URL (backend only)
  },
  cohere: {
    baseURL: "https://api.cohere.ai/compatibility/v1",
  },
  perplexity: {
    baseURL: "https://api.perplexity.ai",
  },
};

// Default provider
let currentProvider = "openai";

// List of observers to notify when provider changes
const providerChangeObservers = [];

// Add observer for provider changes
export const addProviderChangeObserver = (callback) => {
  providerChangeObservers.push(callback);
  return () => {
    const index = providerChangeObservers.indexOf(callback);
    if (index !== -1) providerChangeObservers.splice(index, 1);
  };
};

/**
 * Switch the current provider based on model name
 * @param {string} model - The model name to use
 * @returns {Object} - The provider instance
 */
export function switchProvider(model) {
  if (!model) {
    currentProvider = "openai";
    return providerFactory.getProvider("openai");
  }

  // Determine provider from model name
  let provider = "openai";

  // Check model name to determine appropriate provider
  if (model.includes("claude")) provider = "anthropic";
  else if (model.includes("grok")) provider = "xai";
  else if (model.includes("mistral") && !model.includes("mixtral"))
    provider = "mistral";
  else if (model.includes("deepseek")) provider = "deepseek";
  else if (model.includes("gemini")) provider = "gemini";
  else if (model.includes("qwen")) provider = "qwen";
  else if (model.includes("llama3") || model.includes("mixtral"))
    provider = "groq";
  else if (model.includes("command")) provider = "cohere";
  else if (model.includes("sonar") || model === "r1-1776")
    provider = "perplexity";

  // Update the current provider tracking
  const previousProvider = currentProvider;
  currentProvider = provider;

  // Notify any observers of the provider change
  providerChangeObservers.forEach((callback) => {
    try {
      callback({
        previousProvider,
        currentProvider: provider,
        model,
      });
    } catch (error) {
      // Silent error handling
    }
  });

  // Return the provider instance
  return providerFactory.getProvider(provider, { defaultModel: model });
}

// Get the current provider name
export const getCurrentProvider = () => currentProvider;

// Get provider config (simplified - no API keys)
export const getProviderConfig = (providerName = currentProvider) => {
  return providers[providerName] || providers.openai;
};

// Export a placeholder object for compatibility
// All actual AI requests should use the backend service
export const openai = {
  provider: currentProvider,
  note: "All AI requests are handled through the backend service",
};

// Create custom instances - now returns backend service reference
export const createOpenAI = (options = {}) => {
  console.warn(
    "createOpenAI called - all AI requests should use backend service"
  );
  return {
    provider: currentProvider,
    options,
    note: "Use BackendProviderService for AI requests",
  };
};

export default openai;
